// Each product's JSON is shared by its collection cards. Cache by DOM node so a
// replaced AJAX result with the same element ID is parsed afresh.
const zentrobadCardDataCache = new WeakMap();

class ZentrobadCardColorPicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') {
      if (this.selectedVariant) this.updateImage(this.selectedVariant);
      return;
    }
    this.dataset.ready = 'true';

    this.card = this.closest('[data-zb-color-card]');
    this.buttons = [...this.querySelectorAll('[data-zb-card-option-value]')];
    this.data = this.readData();
    const variantPreview = this.card?.closest('[data-zb-preview-variant-id]');
    const availableVariants = (this.data.variants || []).filter((variant) => variant.available || variantPreview);
    const basinCard = this.card?.closest('[data-zb-basin-option-position]');
    const basinPosition = basinCard?.dataset.zbBasinOptionPosition;
    const basinValue = basinCard?.dataset.zbBasinValue;
    const executionCard = this.card?.closest('[data-zb-execution-option-position]');
    this.variants = executionCard
      ? (this.data.variants || []).filter((variant) =>
          variant[`option${executionCard.dataset.zbExecutionOptionPosition}`] === executionCard.dataset.zbExecutionValue)
      : basinPosition && basinValue
      ? availableVariants.filter((variant) => variant[`option${basinPosition}`] === basinValue)
      : availableVariants;
    if (!this.card || this.variants.length === 0) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-zb-card-option-value]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      this.selectOption(button);
    });

    const previewGroup = this.card.closest('[data-zb-preview-color-position]');
    const initialVariants = previewGroup
      ? this.variants.filter((variant) =>
          variant[`option${previewGroup.dataset.zbPreviewColorPosition}`] === previewGroup.dataset.zbPreviewColor &&
          variant[`option${previewGroup.dataset.zbPreviewGripPosition}`] === previewGroup.dataset.zbPreviewGrip)
      : this.variants;
    const initialVariant = variantPreview
      ? this.variants.find((variant) => String(variant.id) === variantPreview.dataset.zbPreviewVariantId)
      : initialVariants[Math.floor(Math.random() * initialVariants.length)];
    this.applyVariant(initialVariant || this.variants[0]);
  }

  readData() {
    try {
      const source = this.dataset.zbCardDataRef
        ? document.getElementById(this.dataset.zbCardDataRef)
        : this.querySelector('[data-zb-card-variant-data]');
      if (!source) return {};
      if (!zentrobadCardDataCache.has(source)) {
        zentrobadCardDataCache.set(source, JSON.parse(source.textContent || '{}'));
      }
      return zentrobadCardDataCache.get(source);
    } catch (error) {
      console.warn('Zentrobad card variant data could not be read.', error);
      return {};
    }
  }

  disconnectedCallback() {
    this.cancelImageLoad?.();
  }

  selectOption(button) {
    const position = Number(button.dataset.optionPosition);
    const selections = this.currentSelections();
    selections[position] = button.dataset.optionValue;

    const variant = this.findMatchingVariant(selections, position);
    if (variant) this.applyVariant(variant);
  }

  currentSelections() {
    const selections = {};
    if (this.selectedVariant) {
      [1, 2, 3].forEach((position) => {
        const value = this.selectedVariant[`option${position}`];
        if (value) selections[position] = value;
      });
    }
    return this.buttons.reduce((selections, button) => {
      if (button.getAttribute('aria-pressed') === 'true') {
        selections[Number(button.dataset.optionPosition)] = button.dataset.optionValue;
      }
      return selections;
    }, selections);
  }

  findMatchingVariant(selections, changedPosition) {
    const exact = this.variants.find((variant) =>
      Object.entries(selections).every(([position, value]) => variant[`option${position}`] === value)
    );
    return exact || null;
  }

  applyVariant(variant) {
    if (!variant) return;
    this.selectedVariant = variant;

    this.buttons.forEach((button) => {
      const selected = variant[`option${button.dataset.optionPosition}`] === button.dataset.optionValue;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.updateAvailability(variant);
    this.updateLinks(variant.url);
    this.updateImage(variant);
    this.updatePrice(variant);
    this.updateAddButtons(variant.id);
    this.updateBadges(variant);
    const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    this.card.querySelectorAll('[data-zb-card-options]').forEach((label) => {
      label.textContent = optionValues.join(' · ');
    });
    const gridItem = this.card.closest('.product-grid__item');
    if (gridItem && this.data.options) {
      gridItem.dataset.finderColor = this.data.options.flatMap((name, index) =>
        /farbe|color/i.test(name) ? [variant[`option${index + 1}`]] : []).filter(Boolean).join('|');
    }
    this.card.querySelectorAll('[data-zb-card-availability]').forEach((label) => { label.hidden = variant.available; });
    let title = this.data.variantTitles?.[String(variant.id)]?.replace(/\s+Zentrobad\s*$/i, '');
    const basinValue = this.card.closest('[data-zb-basin-value]')?.dataset.zbBasinValue;
    if (title && basinValue && !/\bBecken\b/i.test(title)) {
      const basinLabel = ` / Becken ${basinValue}`;
      title = /\s+\d+\s+Schubladen\b/.test(title)
        ? title.replace(/(\s+\d+\s+Schubladen\b)/, `${basinLabel}$1`)
        : `${title}${basinLabel}`;
    }
    if (title) {
      this.card.querySelectorAll('[data-zentrobad-variant-title], .product-grid-view-zoom-out--details h3, .product-card__link .visually-hidden, .zentrobad-highrunner-sale__title, .zentrobad-cart-cross-sell__title, .zentrobad-cross-sell__title, .zentrobad-similar-products__title').forEach((element) => { element.textContent = title; });
      this.card.querySelectorAll('a[aria-label]').forEach((link) => { link.setAttribute('aria-label', title); });
    }

    this.card.dataset.zbProductUrl = variant.url;
    this.card.dataset.selectedVariantId = variant.id;
  }

  updateAvailability(selectedVariant) {
    this.buttons.forEach((button) => {
      const position = Number(button.dataset.optionPosition);
      const possible = this.variants.some((variant) => {
        if (variant[`option${position}`] !== button.dataset.optionValue) return false;
        return [1, 2, 3].filter((otherPosition) => otherPosition !== position)
          .every((otherPosition) => variant[`option${otherPosition}`] === selectedVariant[`option${otherPosition}`]);
      });
      button.disabled = !possible;
      button.setAttribute('aria-disabled', possible ? 'false' : 'true');
    });
  }

  updateLinks(url) {
    if (!url) return;
    this.card.querySelectorAll('[data-zb-card-link], [data-zb-product-link], .product-card__link, .card-gallery a[ref="cardGalleryLink"], a[ref="productTitleLink"]').forEach((link) => {
      link.href = url;
    });
  }

  updateImage(variant) {
    const requestId = (this.imageRequestId || 0) + 1;
    this.imageRequestId = requestId;
    this.cancelImageLoad?.();
    const imageUrl = this.imageForVariant(variant);
    const images = [...this.card.querySelectorAll('[data-zb-card-image], .zentrobad-highrunner-sale__media img, .zentrobad-cart-cross-sell__media img, .zentrobad-cross-sell__media img, .zentrobad-similar-products__media img, .card-gallery slideshow-slide:not([hidden]) img')];
    if (!images.length) return Promise.resolve(false);
    if (!imageUrl) {
      this.setImageState('error');
      return Promise.resolve(false);
    }

    const title = this.data.variantTitles?.[String(variant.id)]?.replace(/\s+Zentrobad\s*$/i, '');
    const updateAlt = () => images.forEach((image) => {
      image.alt = title || `${this.data.productTitle || image.alt} - ${[variant.option1, variant.option2, variant.option3].filter(Boolean).join(', ')}`;
    });
    if (images.every((image) => image.src === imageUrl && image.complete && image.naturalWidth > 0)) {
      updateAlt();
      this.setImageState('ready');
      return Promise.resolve(true);
    }

    this.setImageState('loading');
    return new Promise((resolve) => {
      const loader = new Image();
      let finished = false;
      let decoding = false;
      let observer;
      const finish = (success) => {
        if (finished) return;
        finished = true;
        loader.onload = null;
        loader.onerror = null;
        observer?.disconnect();
        if (this.cancelImageLoad === cancel) this.cancelImageLoad = null;
        resolve(success);
      };
      const cancel = () => finish(false);
      this.cancelImageLoad = cancel;
      const isCurrent = () => !finished && this.imageRequestId === requestId;
      const fail = () => {
        if (isCurrent()) this.setImageState('error');
        finish(false);
      };
      const showImage = async () => {
        if (decoding || !isCurrent()) return;
        decoding = true;
        try {
          if (loader.decode) await loader.decode();
          if (!isCurrent()) return finish(false);
          images.forEach((image) => {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = imageUrl;
          });
          updateAlt();
          await Promise.all(images.map((image) => image.decode?.()));
          if (!isCurrent()) return finish(false);
          if (this.card.hasAttribute('data-featured-media-url')) {
            this.card.dataset.featuredMediaUrl = imageUrl;
          }
          this.setImageState('ready');
          finish(true);
        } catch {
          fail();
        }
      };
      loader.onload = showImage;
      loader.onerror = fail;
      const startLoad = () => {
        if (!isCurrent()) return;
        loader.src = imageUrl;
        if (loader.complete && loader.naturalWidth > 0) showImage();
      };
      const media = this.card.querySelector('.card-gallery, .zentrobad-highrunner-sale__media, .zentrobad-cart-cross-sell__media, .zentrobad-cross-sell__media, .zentrobad-similar-products__media');
      if (typeof IntersectionObserver === 'function' && media) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            startLoad();
          }
        }, { rootMargin: '250px 0px' });
        observer.observe(media);
      } else {
        startLoad();
      }
    });
  }

  setImageState(state) {
    this.card.dataset.zbImageState = state;
    const media = this.card.querySelector('.card-gallery, .zentrobad-highrunner-sale__media, .zentrobad-cart-cross-sell__media, .zentrobad-cross-sell__media, .zentrobad-similar-products__media');
    if (!media) return;
    media.classList.add('zentrobad-card-image-container');
    media.dataset.zbImageState = state;
    media.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    let status = media.querySelector('[data-zb-card-image-status]');
    if (!status && state !== 'ready') {
      status = document.createElement('span');
      status.className = 'zentrobad-card-image-status';
      status.setAttribute('data-zb-card-image-status', '');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      media.append(status);
    }
    if (status) {
      status.hidden = state === 'ready';
      status.textContent = state === 'loading' ? 'Bild wird geladen' : state === 'error' ? 'Bild derzeit nicht verfügbar' : '';
    }
  }

  imageForVariant(variant) {
    if ((this.card.closest('[data-zb-preview-variant-id]') || this.card.closest('[data-zb-basin-value]')) && variant.featuredImage) {
      return variant.featuredImage;
    }
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const key = [variant.option1, variant.option2, variant.option3].map(normalize).join('|');
    const group = (this.data.groups || []).find((item) =>
      [item.option1, item.option2, item.option3].map(normalize).join('|') === key
    );
    const groupImage = group?.images?.find((image) => image?.lowRes || image?.highRes);
    return groupImage?.lowRes || groupImage?.highRes || variant.featuredImage || (this.data.variants?.length === 1 ? this.data.fallbackImage : '') || '';
  }

  updatePrice(variant) {
    this.card.querySelectorAll('[data-zb-card-price]').forEach((price) => {
      price.textContent = variant.price;
    });

    this.card.querySelectorAll('product-price').forEach((priceComponent) => {
      priceComponent.querySelectorAll('.price').forEach((price) => {
        price.textContent = variant.price;
      });
      priceComponent.querySelectorAll('.compare-at-price').forEach((price) => {
        price.textContent = variant.compareAtPrice || '';
      });
      priceComponent.querySelector('.price__regular')?.classList.toggle('price__hidden', Boolean(variant.compareAtPrice));
      priceComponent.querySelector('.price__sale')?.classList.toggle('price__hidden', !variant.compareAtPrice);
    });
  }

  updateBadges(variant) {
    this.card.querySelectorAll('[data-zb-card-badge]').forEach((badge) => {
      badge.hidden = variant.available && !variant.compareAtPrice;
      badge.textContent = variant.available ? badge.dataset.saleLabel : badge.dataset.soldOutLabel;
      badge.classList.remove(badge.dataset.saleClass, badge.dataset.soldOutClass);
      badge.classList.add(variant.available ? badge.dataset.saleClass : badge.dataset.soldOutClass);
    });
  }

  updateAddButtons(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    this.card.querySelectorAll('quick-add-component input[name="id"]').forEach((input) => {
      input.value = variantId;
      input.disabled = !variant?.available;
    });
    this.card.querySelectorAll('[data-zb-card-add], [data-zentrobad-cross-sell-add]').forEach((button) => {
      button.dataset.variantId = variantId;
    });
  }
}

if (!customElements.get('zentrobad-card-color-picker')) {
  customElements.define('zentrobad-card-color-picker', ZentrobadCardColorPicker);
}
